/**
 * Lead Time Tracking Service
 * Persists explicit and implied time entries to Firestore
 */

import { db } from './firebase';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  Timestamp,
  writeBatch,
  doc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import type { LeadTimeEntry } from '../types';
import { deleteFirestoreDocsInBatches } from './firestoreBatchDelete';

const COLLECTION = 'leadTimeEntries';

function toFirestore(entry: Omit<LeadTimeEntry, 'id'>): Record<string, unknown> {
  return {
    leadId: entry.leadId,
    userId: entry.userId,
    userName: entry.userName,
    type: entry.type,
    minutes: entry.minutes,
    startTime: entry.startTime instanceof Date ? Timestamp.fromDate(entry.startTime) : entry.startTime,
    endTime: entry.endTime instanceof Date ? Timestamp.fromDate(entry.endTime) : entry.endTime,
    note: entry.note ?? null,
    proposalId: entry.proposalId ?? null,
    dealId: entry.dealId ?? null,
    createdAt: entry.createdAt instanceof Date ? Timestamp.fromDate(entry.createdAt) : entry.createdAt,
  };
}

function fromFirestore(docSnap: { id: string; data: () => Record<string, unknown> }): LeadTimeEntry {
  const d = docSnap.data();
  return {
    id: docSnap.id,
    leadId: d.leadId as string,
    userId: d.userId as string,
    userName: d.userName as string,
    type: d.type as LeadTimeEntry['type'],
    minutes: d.minutes as number,
    startTime: (d.startTime as { toDate: () => Date })?.toDate?.() ?? new Date(d.startTime as string),
    endTime: (d.endTime as { toDate: () => Date })?.toDate?.() ?? new Date(d.endTime as string),
    note: d.note as string | undefined,
    proposalId: d.proposalId as string | undefined,
    dealId: d.dealId as string | undefined,
    createdAt: (d.createdAt as { toDate: () => Date })?.toDate?.() ?? new Date(d.createdAt as string),
  };
}

/** Save a single time entry */
export async function saveLeadTimeEntry(entry: Omit<LeadTimeEntry, 'id'>): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), toFirestore(entry));
  return ref.id;
}

/** Save multiple entries in a batch (e.g. periodic implied flushes) */
export async function saveLeadTimeEntries(entries: Omit<LeadTimeEntry, 'id'>[]): Promise<void> {
  if (entries.length === 0) return;
  const col = collection(db, COLLECTION);
  for (let i = 0; i < entries.length; i += 500) {
    const chunk = entries.slice(i, i + 500);
    const batch = writeBatch(db);
    for (const e of chunk) {
      const docRef = doc(col);
      batch.set(docRef, toFirestore(e));
    }
    await batch.commit();
  }
}

/** Update a time entry */
export async function updateLeadTimeEntry(
  id: string,
  updates: Partial<Pick<LeadTimeEntry, 'minutes' | 'startTime' | 'endTime' | 'note'>>
): Promise<void> {
  const ref = doc(db, COLLECTION, id);
  const data: { minutes?: number; note?: string | null; startTime?: Timestamp; endTime?: Timestamp } = {};
  if (updates.minutes !== undefined) data.minutes = updates.minutes;
  if (updates.note !== undefined) data.note = updates.note ?? null;
  if (updates.startTime !== undefined) {
    data.startTime = updates.startTime instanceof Date ? Timestamp.fromDate(updates.startTime) : updates.startTime;
  }
  if (updates.endTime !== undefined) {
    data.endTime = updates.endTime instanceof Date ? Timestamp.fromDate(updates.endTime) : updates.endTime;
  }
  if (Object.keys(data).length > 0) {
    await updateDoc(ref, data);
  }
}

function assertEntryId(id: string): string {
  const t = typeof id === 'string' ? id.trim() : '';
  if (!t) throw new Error('Missing time entry id');
  return t;
}

/** Delete a time entry */
export async function deleteLeadTimeEntry(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, assertEntryId(id)));
}

/** Delete multiple entries (merged auto segments, etc.) — chunked for Firestore limits */
export async function deleteLeadTimeEntries(
  ids: string[],
  onProgress?: (deleted: number, total: number) => void,
): Promise<void> {
  await deleteFirestoreDocsInBatches(COLLECTION, ids.map(assertEntryId), onProgress);
}

/** Fetch lead entries tagged to one deal. Untagged lead time is loaded by lead id. */
export async function getLeadTimeEntriesByDeal(dealId: string): Promise<LeadTimeEntry[]> {
  const q = query(collection(db, COLLECTION), where('dealId', '==', dealId));
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map(d => fromFirestore({ id: d.id, data: () => d.data() }))
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

/** Fetch all time entries for a lead */
export async function getLeadTimeEntries(leadId: string): Promise<LeadTimeEntry[]> {
  const q = query(collection(db, COLLECTION), where('leadId', '==', leadId));
  const snapshot = await getDocs(q);
  const entries = snapshot.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }));
  return entries.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

/** Fetch all time entries (for analytics) */
export async function getAllLeadTimeEntries(options?: {
  userId?: string;
  startDate?: Date;
  endDate?: Date;
}): Promise<LeadTimeEntry[]> {
  const snapshot = await getDocs(collection(db, COLLECTION));
  let entries = snapshot.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }));

  if (options?.userId) {
    entries = entries.filter(e => e.userId === options.userId);
  }
  if (options?.startDate) {
    entries = entries.filter(e => e.endTime >= options.startDate!);
  }
  if (options?.endDate) {
    entries = entries.filter(e => e.startTime <= options.endDate!);
  }

  return entries.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

/** On-demand date-range fetch for the Settings team activity report. */
export async function getLeadTimeEntriesInRange(options: {
  start: Date;
  end: Date;
}): Promise<LeadTimeEntry[]> {
  const q = query(
    collection(db, COLLECTION),
    where('startTime', '>=', Timestamp.fromDate(options.start)),
    where('startTime', '<=', Timestamp.fromDate(options.end)),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map((d) => fromFirestore({ id: d.id, data: () => d.data() }))
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}
